import React, { memo, useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { preserveStubsUrl } from 'utils/general';

import { pushMenuClick } from 'utils/gtm';

import urls from 'constants/urls';

import { dataLayer } from 'analytics/data-layer';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import { MyWaitroseCard } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Cards';
import {
  DocumentAdd,
  User,
  Thumbnails,
  Van,
  Document,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import SignOutButton from 'components/AuthenticationAction/SignOut/Button';
import { LoadableListsPage, LoadableMyOrdersPage } from 'components/App/loadableComponents';
import useDeliveryPassStatus from 'components/BookSlot/contexts/DeliveryPassStatus/useDeliveryPassStatus';
import { DeliveryPassStatusEnum } from 'components/BookSlot/contexts/DeliveryPassStatus/DeliveryPassStatusContext';

import { useOnClickOutside } from 'hooks';
import styles from './MyAccount.scss';

export const myAccountLabels = {
  myWaitroseLoyalty: 'My Waitrose loyalty',
  lists: 'Lists',
  accountOverview: 'Account overview',
  myOrders: 'My orders',
  favourites: 'Favourites',
  signOut: 'Sign out',
  deliveryPass: 'Delivery Pass',
};

const logAnalytics = button => {
  dataLayer.push({
    event: 'account_menu_click',
    my_account_menu: {
      button,
    },
    temporary: true,
  });
};

const MyAccount = memo(() => {
  const [hidden, setHidden] = useState(true);
  const insideRef = useRef();
  const componentPreloadRef = useRef({
    myDetailsPage: false,
    listsPage: false,
  });
  const deliveryPassStatus = useDeliveryPassStatus();

  const toggleVisibility = useCallback(() => {
    setHidden(!hidden);
  }, [hidden]);

  const handleButtonClick = () => {
    if (hidden) {
      logAnalytics('Header');
    }
    toggleVisibility();
  };

  useOnClickOutside(insideRef, () => {
    if (!hidden) {
      setHidden(true);
    }
  });

  const handleBlur = useCallback(() => {
    if (!hidden) {
      toggleVisibility();
    }
  }, [hidden, toggleVisibility]);

  const handleFocus = useCallback(() => {
    if (hidden) {
      toggleVisibility(false);
    }
  }, [hidden, toggleVisibility]);

  const handleLinkClick = useCallback(
    event => {
      const button = event.target.textContent;

      toggleVisibility();
      pushMenuClick(`My Account: ${button}`);
      logAnalytics(button);
    },
    [toggleVisibility],
  );

  const preloadLists = useCallback(() => {
    if (componentPreloadRef.current.listsPage) {
      return;
    }

    componentPreloadRef.current.listsPage = true;
    LoadableListsPage.preload();
  }, []);

  const preloadMyOrders = useCallback(() => {
    if (componentPreloadRef.current.myOrdersPage) {
      return;
    }

    componentPreloadRef.current.myOrdersPage = true;
    LoadableMyOrdersPage.preload();
  }, []);

  return (
    <nav
      aria-labelledby="myAccountAuthenticated"
      ref={insideRef}
      className={styles.accountActionWrapper}
      id="authenticated"
      data-testid="my-account-menu"
    >
      <button
        aria-controls="account-actions"
        aria-expanded={!hidden}
        className={classNames(styles.button)}
        data-testid="my-account-button"
        id="account-details"
        onClick={handleButtonClick}
        tabIndex={-1}
        type="button"
      >
        <User className={styles.buttonIcon} size="small" />
        <span id="myAccountAuthenticated" className={styles.btnText}>
          Account
        </span>
      </button>
      <div className={styles.accountPopupWrapper}>
        <div className={classNames(styles.accountPopup, { 'sr-only': hidden })}>
          <ul className={styles.accountActions} id="account-actions">
            <li>
              <TextLink
                component="a"
                data-testid="account-overview-link"
                id="my-details"
                onBlur={handleBlur}
                onClick={handleLinkClick}
                onFocus={handleFocus}
                href={preserveStubsUrl(urls.myAccount)}
                underline="hover"
              >
                <Thumbnails className={styles.accountMenuIcon} size="medium" />
                {myAccountLabels.accountOverview}
              </TextLink>
            </li>
            <li>
              <TextLink
                component={Link}
                data-testid="my-orders-link"
                id="my-details"
                onBlur={handleBlur}
                onClick={handleLinkClick}
                onFocus={handleFocus}
                onMouseEnter={preloadMyOrders}
                to={urls.myOrdersPage}
                underline="hover"
              >
                <Document className={styles.accountMenuIcon} size="medium" />
                {myAccountLabels.myOrders}
              </TextLink>
            </li>
            <li>
              <TextLink
                component={Link}
                data-testid="lists-link"
                onBlur={handleBlur}
                onClick={handleLinkClick}
                onFocus={handleFocus}
                onMouseEnter={preloadLists}
                to={urls.lists}
                underline="hover"
              >
                <DocumentAdd className={styles.accountMenuIcon} size="medium" />
                {myAccountLabels.lists}
              </TextLink>
            </li>
            {[DeliveryPassStatusEnum.ACTIVE, DeliveryPassStatusEnum.AVAILABLE].includes(
              deliveryPassStatus,
            ) ? (
              <li>
                <TextLink
                  data-testid="deliveryPass-link"
                  onBlur={handleBlur}
                  onClick={handleLinkClick}
                  onFocus={handleFocus}
                  href={
                    deliveryPassStatus === DeliveryPassStatusEnum.ACTIVE
                      ? urls.deliveryPassActive
                      : urls.deliveryPassAvailable
                  }
                  underline="hover"
                >
                  <Van className={styles.accountMenuIcon} size="medium" />
                  {myAccountLabels.deliveryPass}
                </TextLink>
              </li>
            ) : null}
            <li>
              <TextLink
                data-testid="my-waitrose-link"
                onBlur={handleBlur}
                onClick={handleLinkClick}
                onFocus={handleFocus}
                underline="hover"
                href={urls.myWaitrosePage}
              >
                <MyWaitroseCard className={styles.accountMenuIcon} size="medium" />
                {myAccountLabels.myWaitroseLoyalty}
              </TextLink>
            </li>
            <li className={styles.signOut}>
              <SignOutButton
                data-testid="account-sign-out"
                className={styles.signOutButton}
                onClick={handleLinkClick}
                onBlur={handleBlur}
                onFocus={handleFocus}
                width="full"
              />
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
});

MyAccount.displayName = 'MyAccount';

export default MyAccount;
